import React, { useEffect, useState } from "react";
import { useLandingContent } from "../../lib/hooks/useContent";

type ContactInfo = {
  title?: string;                 // "Bracket & Brace"
  email?: string;
  phone?: string;
  copy?: string;
  contact_us_title?: string;      // “Contact Us” (top)
  subject_placeholder?: string;
  message_placeholder?: string;
  address?: string;               // optional
};

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function Contact() {
  const { data, loading, errors } = useLandingContent() as {
    data?: { contact?: ContactInfo };
    loading?: boolean;
    errors?: Record<string, string>;
  };
  const contact = data?.contact;

  const [open, setOpen] = useState(false);
  const [appeared, setAppeared] = useState(false); // enter/exit animation
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const raf = requestAnimationFrame(() => setAppeared(true));
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prev;
      setAppeared(false);
    };
  }, [open]);

  const closeModal = () => {
    setAppeared(false);
    setTimeout(() => setOpen(false), 200);
  };

  if (loading && !contact) return <p>Loading contact info…</p>;
  if (errors?.contact && !contact) return <p>Failed to load contact info.</p>;

  const headingTop = contact?.contact_us_title || "Contact Us";
  const parts = headingTop.split(/\s+/);
  const lastWord = parts.pop() ?? "";
  const leadTitle = parts.join(" ");
  const subjPH = contact?.subject_placeholder || "Subject";
  const msgPH  = contact?.message_placeholder || "Your message…";
  //const orgTitle = contact?.title || "Bracket & Brace";

  const emailHref = contact?.email ? `mailto:${encodeURIComponent(contact.email)}` : undefined;
  const phoneHref = contact?.phone ? `tel:${contact.phone.replace(/[^+\d]/g, "")}` : undefined;

  return (
   <section
  id="contact"
  className="text-[#DEE3F2] bg-[linear-gradient(to_bottom,_#080D1B_50%,_#000000_50%)] py-0"
>
  <div className="max-w-6xl mx-auto flex flex-col">
    {/* 🌟 TOP HALF: animation + button */}
    <div className="flex flex-col items-center justify-center gap-4 py-12 px-4 sm:px-6 lg:px-10 text-center">
      {/* ✉️ Mail Animation */}
      <div className="flex justify-center items-center relative">
        <div
          className={`relative w-[80px] h-[60px] transition-transform duration-700 ease-in-out ${
            isHovered ? "translate-y-2" : ""
          }`}
        >
          {/* Envelope Base */}
          <div className="absolute bottom-0 w-full h-[40px] bg-[#96ADE4] rounded-b-md"></div>

          {/* Top Flap */}
          <div
            className="absolute bottom-[11px] left-0 w-0 h-0 border-l-[40px] border-r-[40px] border-t-[30px] 
                       border-l-transparent border-r-transparent border-t-[#7393E2] origin-bottom transition-transform 
                       duration-700 ease-in-out"
            style={{
              transformOrigin: "50% 0%",
              transform: isHovered ? "rotateX(-180deg)" : "rotateX(0deg)",
            }}
          ></div>

          {/* Letter */}
          <div
            className={`absolute left-[10px] bottom-[5px] w-[60px] h-[25px] bg-white rounded-sm transition-all 
                        duration-700 ease-in-out ${
                          isHovered ? "translate-y-[-25px] opacity-100" : "opacity-0"
                        }`}
          ></div>
        </div>
      </div>

      {/* Title */}
      <h3 className="text-2xl sm:text-3xl font-bold">
        {leadTitle}{leadTitle && " "}
        <span className="text-[#2B61DE]">{lastWord}</span>
      </h3>

      {/* Contact Button */}
      <button
        type="button"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center px-6 py-2 rounded-lg bg-white text-black font-semibold 
                   hover:opacity-90 transition shadow-[0_10px_20px_rgba(0,0,0,0.25)]"
      >
        Contact Us
      </button>
    </div>

    {/* ⚫️ BOTTOM HALF: black area */}
    <div className="bg-black py-10 px-4 sm:px-6 lg:px-10">
      <h4 className="text-lg sm:text-xl font-semibold text-white">Bracket & Brace</h4>
      <hr className="border-white/20 my-3"/>

      
      {/* Contact columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 lg:gap-x-12 w-full ">
        {/* Column 1 */}
        <div className="flex flex-col gap-2">
          <a href={emailHref} className="hover:opacity-90 transition-opacity break-words" target="_blank" rel="noreferrer">
            {contact?.email || "hello@example.com"}
          </a>
          <a href="https://x.com/bracket__brace" target="_blank" rel="noreferrer" className="hover:opacity-90">
            Twitter
          </a>
          <a href="https://www.tiktok.com/@bracketandbrace" target="_blank" rel="noreferrer" className="hover:opacity-90">
            TikTok
          </a>
        </div>

        {/* Column 2 */}
        <div className="flex flex-col gap-2 lg:px-10">
          <a href={phoneHref} className="hover:opacity-90" title={contact?.phone}>
            {contact?.phone || "(555) 555-5555"}
          </a>
          <a href="https://www.instagram.com/bracketandbrace.dev" target="_blank" rel="noreferrer" className="hover:opacity-90">
            Instagram
          </a>
          <a href="https://github.com/bracketandbrace" target="_blank" rel="noreferrer" className="hover:opacity-90">
            GitHub
          </a>
        </div>

        {/* Column 3 */}
        <div className="flex flex-col gap-2 lg:px-10">
          <a href="https://bracketandbrace.medium.com/" target="_blank" rel="noreferrer" className="hover:opacity-90">
            Medium
          </a>
          <a href="https://www.youtube.com/@BracketandBrace" target="_blank" rel="noreferrer" className="hover:opacity-90">
            YouTube
          </a>
          <a href="https://www.linkedin.com/company/bracket-and-brace/" target="_blank" rel="noreferrer" className="hover:opacity-90">
            LinkedIn
          </a>
        </div>
      </div>

      <hr className="border-white/20 my-3" />
      <p className="text-xs text-left text-gray-300">
        Copyright © 2025 Bracket & Brace. All rights reserved.
      </p>
    </div>
  </div>
  {open && (
        <div
          className={
            "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm " +
            "transition-opacity duration-200 " +
            (appeared ? "opacity-100" : "opacity-0")
          }
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-modal-title"
          onClick={closeModal}
        >
          <div
            className={
              "relative w-full max-w-lg bg-[#1e2a5c] p-6 rounded-xl shadow-2xl " +
              "transition-all duration-200 " +
              (appeared ? "opacity-100 scale-100" : "opacity-0 scale-95")
            }
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              className="absolute top-3 right-3 p-2 rounded hover:bg-white/10"
              onClick={closeModal}
              aria-label="Close"
            >
              <XIcon className="w-5 h-5 text-white/80" />
            </button>

            <h3 id="contact-modal-title" className="text-xl font-semibold mb-4 text-center">
              {headingTop}
            </h3>

            <form className="flex flex-col space-y-4">
              <input
                type="email"
                placeholder="Your email"
                className="px-4 py-2 rounded-lg bg-[#2c3e70] text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <input
                type="text"
                placeholder={subjPH}
                className="px-4 py-2 rounded-lg bg-[#2c3e70] text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <textarea
                placeholder={msgPH}
                className="px-4 py-2 rounded-lg bg-[#2c3e70] text-white placeholder-gray-300 h-48 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                type="button"
                className="px-6 py-2 rounded-lg text-white font-semibold bg-gradient-to-r from-[#2639A8] to-[#090E3A] hover:from-[#090E3A] hover:to-[#2639A8] transition-all duration-300"
                onClick={closeModal}
              >
                Submit
              </button>
            </form>
          </div>
        </div>
      )}
</section>
  );
}
